import React, { useEffect, useMemo, useRef, useState } from 'react';
import { UseFeedbackChat } from './useFeedbackChat';
import { ListView } from './FeedbackListView';
import { capturePage } from './env';
import { ChatMessage, FeedbackCategory } from './types';
import { drainPendingCapturedIds } from './capturedIdBuffer';

interface Props {
  chat: UseFeedbackChat;
  appCode: string;
  onSubmitted?: (id: number) => void;
  onClose?: () => void;
  /** true = panel 모드 (닫기 버튼 노출) */
  compact?: boolean;
}

type TabKey = 'compose' | 'list' | 'confirm';

import { stripHtmlToText } from './htmlText';

const ID_PATTERN = /^#?[a-z][a-z0-9-]*$/i;

const emitCaptureOnce = (on: boolean) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('dev-id-inspector:capture-once', { detail: { on } }));
};

export const FeedbackChat: React.FC<Props> = ({ chat, appCode, onSubmitted, onClose, compact }) => {
  const [tab, setTab] = useState<TabKey>('compose');
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Inspector 가 자체 UI(취소/한번클릭)에서 capture-once 해제 시 같이 반영
  useEffect(() => {
    const onEvt = (e: Event) => {
      const detail = (e as CustomEvent<{ on?: boolean }>).detail;
      if (detail && typeof detail.on === 'boolean') setCapturing(detail.on);
    };
    window.addEventListener('dev-id-inspector:capture-once', onEvt);
    return () => window.removeEventListener('dev-id-inspector:capture-once', onEvt);
  }, []);

  /** textarea 커서 위치에 `#id ` 삽입 + 작성 탭으로 이동 (Inspector 캡처 · 런처 `+` 버퍼 공용) */
  const insertIdAtCursor = (id: string) => {
    const insert = `#${id} `;
    const ta = textareaRef.current;
    if (ta) {
      const s = ta.selectionStart ?? ta.value.length;
      const en = ta.selectionEnd ?? s;
      setInput((prev) => prev.slice(0, s) + insert + prev.slice(en));
      // 다음 렌더에서 커서 위치 조정
      requestAnimationFrame(() => {
        try {
          ta.focus();
          const next = s + insert.length;
          ta.setSelectionRange(next, next);
        } catch { /* noop */ }
      });
    } else {
      setInput((prev) => (prev.endsWith(' ') || prev === '' ? prev + insert : prev + ' ' + insert));
    }
    // 작성 탭으로 이동해 즉시 편집 가능하게
    setTab('compose');
  };

  // Inspector 로부터 id 캡처 이벤트 수신 → textarea 커서 위치에 #id 자동 삽입
  useEffect(() => {
    const onCaptured = (e: Event) => {
      const detail = (e as CustomEvent<{ id?: string }>).detail;
      if (!detail?.id) return;
      insertIdAtCursor(detail.id);
    };
    window.addEventListener('dev-id-inspector:captured', onCaptured);
    return () => window.removeEventListener('dev-id-inspector:captured', onCaptured);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 런처 `+` 로 수집한 id — 패널이 닫혀 있어 이 컴포넌트가 없던 동안 버퍼에 쌓인 것을 마운트 직후 삽입
  useEffect(() => {
    const ids = drainPendingCapturedIds();
    if (ids.length === 0) return;
    const ta = textareaRef.current;
    const joined = ids.map((id) => `#${id} `).join('');
    setInput((prev) => (prev === '' || prev.endsWith(' ') ? prev + joined : `${prev} ${joined}`));
    setTab('compose');
    requestAnimationFrame(() => {
      try {
        ta?.focus();
        const len = ta?.value.length ?? 0;
        ta?.setSelectionRange(len, len);
      } catch { /* noop */ }
    });
  }, []);

  const handleAppendText = () => {
    const v = input.trim();
    if (!v) return;
    if (ID_PATTERN.test(v)) chat.addId(v);
    else chat.addText(v);
    setInput('');
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.kind === 'file' && it.type.startsWith('image/')) {
          const f = it.getAsFile();
          if (f) {
            chat.addImage(f);
            e.preventDefault();
            return;
          }
        }
      }
    }
    // HTML 붙여넣기(dev-tools 'Copy element' 등) → 태그 제거 후 보이는 텍스트만 삽입.
    // 평문 붙여넣기는 이 분기를 타지 않고 브라우저 기본 동작 유지. (navido d80a434)
    const html = e.clipboardData?.getData('text/html');
    if (html && /<[a-z][^>]*>/i.test(html)) {
      const stripped = stripHtmlToText(html);
      if (stripped) {
        e.preventDefault();
        const ta = textareaRef.current;
        if (ta) {
          const s = ta.selectionStart ?? ta.value.length;
          const en = ta.selectionEnd ?? s;
          setInput((prev) => prev.slice(0, s) + stripped + prev.slice(en));
          requestAnimationFrame(() => {
            try {
              ta.focus();
              const next = s + stripped.length;
              ta.setSelectionRange(next, next);
            } catch { /* noop */ }
          });
        } else {
          setInput((prev) => prev + stripped);
        }
      }
    }
  };

  const handleFiles = (fs: FileList | null) => {
    if (!fs) return;
    for (let i = 0; i < fs.length; i++) {
      const f = fs.item(i);
      if (!f) continue;
      if (!f.type.startsWith('image/')) continue;
      chat.addImage(f);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      const leftover = input.trim();
      // messages 상태에 addText 로 미리 넣지 않고, submit 파라미터로 넘겨 즉시 반영.
      const id = await chat.submit(leftover || undefined);
      setInput('');
      onSubmitted?.(id);
    } catch (e) {
      const msg = e instanceof Error ? e.message : '제출 실패';
      setError(msg);
    }
  };

  const toggleCapture = () => {
    const next = !capturing;
    setCapturing(next);
    emitCaptureOnce(next);
  };

  /** 현재 뷰포트에서 사용자가 보고 있는 콘텐츠 섹션 id 를 찾아 textarea 커서에 '#id ' 삽입 + 클립보드 복사 */
  const insertCurrentLocation = async () => {
    if (typeof document === 'undefined') return;
    const vw = window.innerWidth, vh = window.innerHeight;
    const viewportArea = vw * vh;

    const isSkipContainer = (el: Element): boolean =>
      !!el.closest('[data-dev-feedback-panel]') ||
      !!el.closest('[data-dev-feedback-launcher]') ||
      !!el.closest('[data-dev-id-inspector]') ||
      !!el.closest('[data-dev-id-inspector-outlines]');

    // 사용자 관점 콘텐츠 섹션 판정: sec-* / page-* prefix 또는 SECTION/ARTICLE 태그
    const isContentSection = (el: HTMLElement): boolean => {
      if (!el.id) return false;
      if (el.id === 'root' || el.id === 'app' || el.id === '__next') return false;
      if (el.id.startsWith('sec-')) return true;
      if (el.id.startsWith('page-')) return true;
      const tag = el.tagName;
      if (tag === 'SECTION' || tag === 'ARTICLE') return true;
      return false;
    };

    // 1차: 뷰포트 중앙에서 위로 올라가며 첫 콘텐츠 섹션 찾기 (사용자가 실제 보고 있는 곳)
    let picked: HTMLElement | null = null;
    let cur: Element | null = document.elementFromPoint(vw / 2, vh / 2);
    while (cur && cur !== document.body) {
      if (!isSkipContainer(cur) && cur instanceof HTMLElement && isContentSection(cur)) {
        picked = cur;
        break;
      }
      cur = cur.parentElement;
    }

    // 2차: 못 찾으면 뷰포트 내 가장 큰 visible area 를 가진 콘텐츠 섹션
    if (!picked) {
      const nodes = document.querySelectorAll<HTMLElement>('body [id], body section, body article');
      let bestArea = 0;
      nodes.forEach((el) => {
        if (isSkipContainer(el)) return;
        if (!isContentSection(el)) return;
        const r = el.getBoundingClientRect();
        if (r.width < 80 || r.height < 80) return;
        const left = Math.max(0, r.left), top = Math.max(0, r.top);
        const right = Math.min(vw, r.right), bottom = Math.min(vh, r.bottom);
        const visW = right - left, visH = bottom - top;
        if (visW <= 0 || visH <= 0) return;
        const area = visW * visH;
        if (area >= viewportArea * 0.98) return;
        if (area > bestArea) { bestArea = area; picked = el; }
      });
    }

    // 3차: 그래도 없으면 가장 큰 임의 id 요소 (wrapper 껍데기 제외)
    if (!picked) {
      const nodes = document.querySelectorAll<HTMLElement>('body [id]');
      let bestArea = 0;
      nodes.forEach((el) => {
        if (isSkipContainer(el)) return;
        if (el.id === 'root' || el.id === 'app' || el.id === '__next') return;
        const r = el.getBoundingClientRect();
        if (r.width < 80 || r.height < 80) return;
        const left = Math.max(0, r.left), top = Math.max(0, r.top);
        const right = Math.min(vw, r.right), bottom = Math.min(vh, r.bottom);
        const visW = right - left, visH = bottom - top;
        if (visW <= 0 || visH <= 0) return;
        const area = visW * visH;
        if (area >= viewportArea * 0.98) return;
        if (area > bestArea) { bestArea = area; picked = el; }
      });
    }

    if (!picked) return;
    const id = picked.id;
    // 클립보드 복사 (silent)
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(`#${id}`);
    } catch { /* noop */ }
    // textarea 커서 위치에 #id 삽입
    const insert = `#${id} `;
    const ta = textareaRef.current;
    if (ta) {
      const s = ta.selectionStart ?? ta.value.length;
      const en = ta.selectionEnd ?? s;
      setInput((prev) => prev.slice(0, s) + insert + prev.slice(en));
      requestAnimationFrame(() => {
        try {
          ta.focus();
          const next = s + insert.length;
          ta.setSelectionRange(next, next);
        } catch { /* noop */ }
      });
    } else {
      setInput((prev) => (prev.endsWith(' ') || prev === '' ? prev + insert : prev + ' ' + insert));
    }
  };

  const imageDrafts = useMemo(
    () =>
      chat.messages
        .map((m, i) => ({ m, i }))
        .filter(({ m }) => m.kind === 'image_ref' && !!m.localFile) as Array<{ m: ChatMessage & { localFile: File }; i: number }>,
    [chat.messages],
  );
  const attachedCount = chat.messages.filter((m) => m.kind === 'image_ref').length;
  const idPillCount = chat.messages.filter((m) => m.kind === 'id').length;
  const textCount = chat.messages.filter((m) => m.kind === 'text').length;
  const draftBadge = attachedCount + idPillCount + textCount;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: 'inherit' }}>
      {/* 탭 헤더 */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid #e5e5e5',
          background: '#fafafa',
          flexShrink: 0,
        }}
      >
        <TabButton active={tab === 'compose'} onClick={() => setTab('compose')} label={`작성${draftBadge > 0 ? ` (${draftBadge})` : ''}`} />
        <TabButton active={tab === 'list'} onClick={() => setTab('list')} label="리스트" />
        <TabButton active={tab === 'confirm'} onClick={() => setTab('confirm')} label="확인요청" />
        <div style={{ flex: 1 }} />
        {compact && onClose && (
          <button
            onClick={onClose}
            title="닫기"
            style={{
              padding: '6px 12px',
              background: 'transparent',
              border: 'none',
              fontSize: 13,
              cursor: 'pointer',
              color: '#666',
            }}
          >
            ×
          </button>
        )}
      </div>

      {tab === 'compose' ? (
        <ComposeView
          input={input}
          setInput={setInput}
          textareaRef={textareaRef}
          onAppendEnter={handleAppendText}
          onPaste={handlePaste}
          onFilesClick={() => fileInputRef.current?.click()}
          fileInputRef={fileInputRef}
          onFilesSelected={handleFiles}
          onCaptureToggle={toggleCapture}
          onInsertCurrentLocation={insertCurrentLocation}
          capturing={capturing}
          imageDrafts={imageDrafts}
          onRemoveAt={chat.removeAt}
          idPillCount={idPillCount}
          reporter={chat.reporter}
          setReporter={chat.setReporter}
          category={chat.category}
          setCategory={chat.setCategory}
          onClear={chat.clear}
          onSubmit={handleSubmit}
          submitting={chat.submitting}
          error={error}
        />
      ) : (
        <ListView key={tab} appCode={appCode} category={tab === 'confirm' ? 'confirm' : undefined} />
      )}
    </div>
  );
};

// ---------- 탭 버튼 ----------
const TabButton: React.FC<{ active: boolean; onClick: () => void; label: string }> = ({ active, onClick, label }) => (
  <button
    onClick={onClick}
    style={{
      padding: '8px 14px',
      background: active ? 'white' : 'transparent',
      border: 'none',
      borderBottom: active ? '2px solid #e94d20' : '2px solid transparent',
      fontWeight: active ? 700 : 500,
      color: active ? '#e94d20' : '#555',
      fontSize: 13,
      cursor: 'pointer',
    }}
  >
    {label}
  </button>
);

// ---------- 작성 뷰 ----------
interface ComposeProps {
  input: string;
  setInput: (v: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  onAppendEnter: () => void;
  onPaste: (e: React.ClipboardEvent) => void;
  onFilesClick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFilesSelected: (fs: FileList | null) => void;
  onCaptureToggle: () => void;
  onInsertCurrentLocation: () => void;
  capturing: boolean;
  imageDrafts: Array<{ m: ChatMessage & { localFile: File }; i: number }>;
  onRemoveAt: (idx: number) => void;
  idPillCount: number;
  reporter: string;
  setReporter: (v: string) => void;
  category: FeedbackCategory;
  setCategory: (v: FeedbackCategory) => void;
  onClear: () => void;
  onSubmit: () => void;
  submitting: boolean;
  error: string | null;
}

const ComposeView: React.FC<ComposeProps> = ({
  input,
  setInput,
  textareaRef,
  onPaste,
  onFilesClick,
  fileInputRef,
  onFilesSelected,
  onCaptureToggle,
  onInsertCurrentLocation,
  capturing,
  imageDrafts,
  onRemoveAt,
  idPillCount,
  reporter,
  setReporter,
  category,
  setCategory,
  onClear,
  onSubmit,
  submitting,
  error,
}) => (
  <div
    style={{
      flex: 1,
      minHeight: 0,
      padding: 8,
      background: 'white',
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    }}
  >
    <div style={{ display: 'flex', flex: 1, minHeight: 0, gap: 6 }}>
      <textarea
        ref={textareaRef}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onPaste={onPaste}
        placeholder={
          '이 화면의 문제를 자유롭게 남기세요.\n' +
          '텍스트 · 이미지 · #id 를 붙여넣을 수 있어요.\n\n' +
          '[최종 제출] 눌러 서버로 보냄'
        }
        style={{
          flex: 1,
          minHeight: 100,
          padding: '10px 12px',
          border: '1px solid #ddd',
          borderRadius: 6,
          fontSize: 14,
          lineHeight: 1.5,
          resize: 'none',
          fontFamily: 'inherit',
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <button
          title="이미지 첨부"
          onClick={onFilesClick}
          style={{
            padding: '8px 10px',
            border: '1px solid #ddd',
            background: '#fafafa',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 16,
          }}
        >
          📷
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            onFilesSelected(e.target.files);
            e.target.value = '';
          }}
        />
        <button
          title="화면에서 id 있는 요소를 탭해 클립보드에 복사"
          onClick={onCaptureToggle}
          style={{
            padding: '8px 6px',
            border: '1px solid ' + (capturing ? '#e94d20' : '#ddd'),
            background: capturing ? '#e94d20' : '#fff8f4',
            color: capturing ? 'white' : '#e94d20',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1.1,
          }}
        >
          {capturing ? '🎯 수집중\n(off)' : '🎯 ID\n수집'}
        </button>
        <button
          title="현재 뷰포트에 가장 크게 보이는 요소의 id 를 자동으로 삽입 · 클립보드 복사"
          onClick={onInsertCurrentLocation}
          style={{
            padding: '8px 6px',
            border: '1px solid #ddd',
            background: '#fafafa',
            color: '#20314D',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1.1,
            whiteSpace: 'pre',
          }}
        >
          {'📍 현재\n위치'}
        </button>
      </div>
    </div>

    {/* 첨부 이미지 미리보기 (드래프트) — 클릭하면 원본 크기 새 탭, × 로 개별 제거 */}
    {imageDrafts.length > 0 && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {imageDrafts.map(({ m, i }) => (
          <ImageThumb key={`${i}-${m.ts}`} file={m.localFile} onRemove={() => onRemoveAt(i)} />
        ))}
      </div>
    )}

    {/* id pill 카운터 (id 는 textarea 안에도 삽입돼 있어 카운터만 표기) */}
    {idPillCount > 0 && (
      <div style={{ display: 'flex', gap: 8, fontSize: 11, color: '#666' }}>
        <span>🏷 id {idPillCount}</span>
      </div>
    )}

    {/* 카테고리 세그먼트 — 운영/개발/기타. 기본 '기타' · 선택은 localStorage 로 유지. */}
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <label style={{ fontSize: 12, color: '#666', minWidth: 40 }}>구분</label>
      {([
        { v: 'operation',   l: '운영', bg: '#1e40af' },
        { v: 'development', l: '개발', bg: '#166534' },
        { v: 'other',       l: '기타', bg: '#616161' },
        { v: 'confirm',     l: '확인요청', bg: '#C2410C' },
      ] as const).map(({ v, l, bg }) => (
        <button
          key={v}
          type="button"
          onClick={() => setCategory(v)}
          style={{
            flex: 1,
            padding: '3px 6px',
            border: '1px solid ' + (category === v ? bg : '#ddd'),
            background: category === v ? bg : '#fafafa',
            color: category === v ? 'white' : '#555',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {l}
        </button>
      ))}
    </div>

    {/* 최종 제출 시 자동 첨부되는 페이지 정보 미리보기 —
        pageId 는 targetIds 에도 auto-inject (관리자 검색 편의). */}
    <CurrentScreenPreview />

    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <label style={{ fontSize: 12, color: '#666' }}>신고자</label>
      <input
        type="text"
        value={reporter}
        onChange={(e) => setReporter(e.target.value)}
        placeholder="비우면 unknown"
        style={{
          flex: 1,
          padding: '4px 8px',
          border: '1px solid #ddd',
          borderRadius: 6,
          fontSize: 12,
        }}
      />
      <button
        onClick={onClear}
        style={{
          padding: '4px 10px',
          background: '#eee',
          border: 'none',
          borderRadius: 6,
          fontSize: 12,
          cursor: 'pointer',
        }}
      >
        초기화
      </button>
      <button
        onClick={onSubmit}
        disabled={submitting}
        style={{
          padding: '4px 12px',
          background: '#2E7D32',
          color: 'white',
          border: 'none',
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          cursor: submitting ? 'wait' : 'pointer',
        }}
      >
        {submitting ? '전송중…' : '최종 제출'}
      </button>
    </div>

    {error && <div style={{ color: '#c62828', fontSize: 12 }}>{error}</div>}
  </div>
);

// ---------- 현재 화면 정보 미리보기 (submit 시 자동 첨부) ----------
// pathname/pageId 는 라우팅 시 바뀌므로 매 render 마다 capturePage() 재호출.
// pageId 는 submit 시 targetIds 에 자동 병합 → 관리자가 화면별 검색 가능.
const CurrentScreenPreview: React.FC = () => {
  const [, force] = useState(0);
  useEffect(() => {
    const onNav = () => force((n) => n + 1);
    window.addEventListener('popstate', onNav);
    return () => window.removeEventListener('popstate', onNav);
  }, []);
  const ctx = capturePage();
  const shortUrl = (() => {
    try {
      const u = new URL(ctx.pageUrl);
      return u.pathname + (u.search || '');
    } catch {
      return ctx.pageUrl || '';
    }
  })();
  return (
    <div
      title={`제출 시 자동 첨부\npageUrl: ${ctx.pageUrl}\npageTitle: ${ctx.pageTitle}\npageId: ${ctx.pageId}\nviewport: ${ctx.viewport}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 8px',
        borderRadius: 6,
        background: '#f5f7fa',
        border: '1px solid #e5e5e5',
        fontSize: 10,
        color: '#4b5563',
        overflow: 'hidden',
      }}
    >
      <span style={{ color: '#e94d20', fontWeight: 700 }}>📍 이 화면</span>
      <span
        style={{
          fontFamily: 'ui-monospace, monospace',
          background: '#fff1eb',
          color: '#e94d20',
          border: '1px solid #ffcbb4',
          borderRadius: 10,
          padding: '0 6px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 160,
        }}
      >
        #{ctx.pageId || '(no-id)'}
      </span>
      <span
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flex: 1,
          minWidth: 0,
        }}
      >
        {shortUrl}
      </span>
    </div>
  );
};

// ---------- 첨부 이미지 썸네일 (드래프트 미리보기) ----------
// URL.createObjectURL 는 revoke 하지 않으면 tab 종료 전까지 메모리 유지 →
// unmount / file 변경 시 revoke 로 즉시 해제.
const ImageThumb: React.FC<{ file: File; onRemove: () => void }> = ({ file, onRemove }) => {
  const [url, setUrl] = useState<string>('');
  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  const sizeKb = Math.round(file.size / 1024);
  return (
    <div
      style={{
        position: 'relative',
        width: 64,
        height: 64,
        border: '1px solid #ddd',
        borderRadius: 6,
        background: '#fafafa',
        overflow: 'hidden',
      }}
      title={`${file.name} (${sizeKb} KB)`}
    >
      {url && (
        <a href={url} target="_blank" rel="noreferrer">
          <img
            src={url}
            alt={file.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </a>
      )}
      <button
        type="button"
        onClick={onRemove}
        title="이 이미지 제거"
        aria-label={`${file.name} 제거`}
        style={{
          position: 'absolute',
          top: 2,
          right: 2,
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: 'none',
          background: 'rgba(0,0,0,0.6)',
          color: 'white',
          fontSize: 11,
          lineHeight: '18px',
          padding: 0,
          cursor: 'pointer',
        }}
      >
        ×
      </button>
    </div>
  );
};
